import React from 'react';
import { ButtonText } from '@saucelabs/ds-button';
import { Col, Row } from '@saucelabs/sl-component-grid';
import { Typography } from '@saucelabs/ds-typography';
import { InlineAlert } from '@saucelabs/ds-alert';

import {
  PRODUCT_API_TESTING,
  PRODUCT_MOBILE_QUALITY,
  ROUTE_SECTION_PURCHASE,
  EMAIL_SUPPORT_PLAN_UPDATE,
  ROUTE_EMAIL_SUPPORT,
  ROUTE_PARENT_SECTION,
  SUBSCRIPTION_STATE,
} from '../../constants';
import type { Subscription } from '../../records/Subscription';
import { getProductType } from '../../records/Plan';
import { Title } from './Title';
import { Card } from '../Layout/Card';
import { PlanDetails } from './PlanDetails';
import { SauceFeature } from './SauceFeature';
import { Price, PriceAddon } from '../Plans/Price';
import { trackEvent, EVENTS } from '../../tracking';
import { productTypeToIcon } from '../../utils';
import { projectsToMonthlyExecutions } from '../../utils/plans';
import { amountSeparator } from '../../utils/numbers';
import { ProductAmount } from './ProductAmount';

interface Props {
  subscription: Subscription
  isUsageCustomer: boolean
}

const trackUpdatePlanEvent = (subscription: Subscription) => {
  const product = getProductType(subscription.plan);
  const payload = {
    product,
    [`${product}_concurrency`]: subscription.getAddonAmount(),
  };
  trackEvent(
    EVENTS.PLANS_CLICK_UPDATE_BUTTON,
    payload,
  );
};

const SubscriptionTile: React.FC<Props> = ({ children, subscription, isUsageCustomer = false }) => {
  const monthlyPriceRounded = subscription.getMonthlyPrice();
  const productAmount = subscription.getAddonAmount();
  const productType = getProductType(subscription.plan);
  const isMobileQuality = productType === PRODUCT_MOBILE_QUALITY;
  const trackUpdatePlan = React.useCallback(
    () => trackUpdatePlanEvent(subscription),
    [subscription]);
  return (
    <Card
      className="p-3 border-solid border-2 border-blue-400 shadow-sm"
    >
      <Row between="xs">
        <Col xs={9}>
          <Title
            icon={productTypeToIcon(productType)}
            isLegacy={!subscription.isUnifiedPlan()}
            name={subscription.displayName()}
          />
        </Col>
        <Col
          className="text-right"
          xs={3}
        >
          <Price>
            {amountSeparator(monthlyPriceRounded)}
            <PriceAddon> / month</PriceAddon>
          </Price>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col xs={9}>
          <div>
            {productAmount && !isMobileQuality && (
              <ProductAmount
                productType={productType}
                secondaryValue={productType === PRODUCT_API_TESTING ?
                  projectsToMonthlyExecutions(productAmount, subscription.add_ons) :
                  undefined}
                value={productAmount}
              />
            )}
            <SauceFeature
              featureName={subscription.displaySauceFeatureName()}
              isMonthly={subscription.isMonthly()}
            />
            <PlanDetails
              nextBillDate={subscription.displayNextBillDate()}
              price={amountSeparator(monthlyPriceRounded)}
            />
          </div>
        </Col>
        <Col
          className="text-right"
          xs={3}
        >
          <ButtonText
            color="primary"
            onPress={trackUpdatePlan}
            size="md"
            to={isUsageCustomer ?
              `/${ROUTE_PARENT_SECTION}/${ROUTE_EMAIL_SUPPORT}?action=${EMAIL_SUPPORT_PLAN_UPDATE}&hide_credits=true` :
              `/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_PURCHASE}`}
            type="button"
            variant="outline"
          >
            {isUsageCustomer ? 'Request Plan Change' : 'Update Plan'}
          </ButtonText>
        </Col>
      </Row>
      {subscription.state === SUBSCRIPTION_STATE.CANCELED && (
        <InlineAlert
          className="mt-3"
          color="warning"
          hideIcon
        >
          <Typography
            className="font-semibold"
            disableSpacing
            variant="body-large"
          >
            The changes you made to your plan will take effect on
            {' '}
            {subscription.displayNextBillDate()}
          </Typography>
          <Typography
            disableSpacing
            variant="body-large"
          >
            {`${subscription.displayName()} plan will be canceled.`}
          </Typography>
        </InlineAlert>
      )}
      {children}
    </Card>
  );
};

export default SubscriptionTile;
export {
  SubscriptionTile,
};
